import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppContext } from '../components/AppContext';
import {
  Container,
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery
} from '@mui/material';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import LabelIcon from '@mui/icons-material/Label';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

import ConceptesAdmin from '../components/gestio-dades/ConceptesAdmin';
import ActiusAdmin from '../components/gestio-dades/ActiusAdmin';
import UsuarisAdmin from '../components/gestio-dades/UsuarisAdmin';
import UsuarisComptesAdmin from '../components/gestio-dades/UsuarisComptesAdmin';

const GestioDadesPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { userapp: routeUserapp } = useParams();
  const navigate = useNavigate();
  const { ctxApp, setCtxApp } = useAppContext();

  const [currentTab, setCurrentTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Compte actiu
  const selectedUserapp = routeUserapp || ctxApp?.userapp || (ctxApp?.comptes && ctxApp.comptes[0]?.userapp) || '';

  const handleAccountChange = (newApp) => {
    if (newApp && newApp !== selectedUserapp) {
      if (setCtxApp) {
        setCtxApp((prev) => ({ ...prev, userapp: newApp }));
      }
      localStorage.setItem('selectedUserApp', newApp);
      navigate(`/${newApp}/gestiodades`);
    }
  };

  const showNotification = useCallback((message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
      {/* Capçalera de la pàgina */}
      <Paper
        elevation={2}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 2,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: 2,
          background: 'linear-gradient(135deg, rgba(25,118,210,0.05) 0%, rgba(255,255,255,1) 100%)',
          borderLeft: '5px solid #1976d2'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <DataUsageIcon color="primary" sx={{ fontSize: { xs: 32, sm: 40 } }} />
          <Box>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold' }}>
              Gestió de Dades
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Administració de catàlegs de conceptes, actius d'inversió, usuaris i permisos de comptes
            </Typography>
          </Box>
        </Box>

        {/* Selector de Compte Actiu per a entitats vinculades a userapp */}
        {(currentTab === 0 || currentTab === 1) && ctxApp?.comptes && ctxApp.comptes.length > 0 && (
          <Box sx={{ minWidth: 200, width: { xs: '100%', sm: 'auto' } }}>
            <FormControl fullWidth size="small">
              <InputLabel>Compte Actiu</InputLabel>
              <Select
                value={selectedUserapp}
                label="Compte Actiu"
                onChange={(e) => handleAccountChange(e.target.value)}
              >
                {ctxApp.comptes.map((acc) => (
                  <MenuItem key={acc.id} value={acc.userapp}>
                    {acc.userapp}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </Paper>

      {/* Pestanyes de navegació */}
      <Paper elevation={1} sx={{ borderRadius: 2, mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={(e, val) => setCurrentTab(val)}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons="auto"
          indicatorColor="primary"
          textColor="primary"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={<LabelIcon />}
            iconPosition="start"
            label="Conceptes"
            sx={{ fontWeight: 'bold', textTransform: 'none' }}
          />
          <Tab
            icon={<TrendingUpIcon />}
            iconPosition="start"
            label="Actius d’Inversió"
            sx={{ fontWeight: 'bold', textTransform: 'none' }}
          />
          <Tab
            icon={<PeopleIcon />}
            iconPosition="start"
            label="Usuaris"
            sx={{ fontWeight: 'bold', textTransform: 'none' }}
          />
          <Tab
            icon={<AccountBalanceIcon />}
            iconPosition="start"
            label="Assignació de Comptes"
            sx={{ fontWeight: 'bold', textTransform: 'none' }}
          />
        </Tabs>

        {/* Contingut de les pestanyes */}
        <Box sx={{ pt: 1, pb: 2 }}>
          {currentTab === 0 && (
            <ConceptesAdmin
              userapp={selectedUserapp}
              showNotification={showNotification}
            />
          )}

          {currentTab === 1 && (
            <ActiusAdmin
              userapp={selectedUserapp}
              showNotification={showNotification}
            />
          )}

          {currentTab === 2 && (
            <UsuarisAdmin
              showNotification={showNotification}
            />
          )}

          {currentTab === 3 && (
            <UsuarisComptesAdmin
              showNotification={showNotification}
            />
          )}
        </Box>
      </Paper>

      {/* Snackbar global de notificacions */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', boxShadow: 3 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default GestioDadesPage;
